/**
 * Iter XX.234 — Visual Coaching v1 renderer.
 *
 * Backend attaches an optional `visual_explanation` payload to the
 * assistant response. This component renders it deterministically as
 * inline SVG — zero image-generation, zero external image assets.
 *
 * Three visual types (locked at v1):
 *   · field_map          — 100×100 cricket field with fielders + regions
 *   · technique_diagram  — body-alignment SVG with optional arrows
 *   · action_plan        — 6-ball strip + cue (XX.233 Current Focus)
 *
 * Confidence tiers (locked at v1):
 *   · high         — "Based on what we've seen in your cricket"
 *   · provisional  — "Something to test"
 *   · generic      — "General concept — one possible explanation"
 *
 * Every element string is validated against a locked vocabulary in
 * `backend/visual_coaching_vocab.py`. Anything not in the vocab
 * is silently dropped by the backend BEFORE it reaches this file, so
 * this component only needs to handle known values.
 *
 * Compatibility contract:
 *   · Renders null (nothing) when payload is missing / malformed.
 *   · Renders null when the visual_coaching_v1_enabled flag is off
 *     (backend already omits the payload; this is belt-and-braces).
 *   · Emits `visual_explanation_engaged` beacon when the parent
 *     Chat.jsx observes the NEXT user turn arriving ≤ 90s after
 *     this component mounts (see `useEngagementBeacon` below).
 */
import { useEffect, useRef } from "react";

// ---- Analytics beacon --------------------------------------------------
async function _emit(kind, payload) {
  try {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    // Reuse the plus_conversion event endpoint — it accepts opaque
    // `kind` strings and writes to audit_events. Failure is silent.
    await fetch(`${base}/api/plus_conversion/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ kind, ...(payload || {}) }),
    });
  } catch { /* analytics failures never affect UX */ }
}


// ---- Confidence chip ---------------------------------------------------
function ConfidenceChip({ confidence, label }) {
  const styles = {
    high:        "bg-emerald-950/60 text-emerald-300 border-emerald-800/50",
    provisional: "bg-amber-950/60 text-amber-300 border-amber-800/50",
    generic:     "bg-neutral-900 text-neutral-400 border-neutral-800",
  };
  const cls = styles[confidence] || styles.generic;
  return (
    <div
      data-testid={`visual-confidence-${confidence}`}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${cls}`}
    >
      {label}
    </div>
  );
}


// ---- FIELD MAP ---------------------------------------------------------
// 100×100 SVG. Field boundary is a circle at (50,50) r=48. Pitch is a
// rectangle down the middle. Fielder positions are looked up against a
// static coordinate table below. `left` handedness mirrors X.
const FIELDER_COORDS = {
  wicketkeeper:          [50, 62],
  first_slip:            [56, 62],
  second_slip:           [59, 62],
  third_slip:            [62, 62],
  gully:                 [65, 60],
  leg_slip:              [44, 62],
  short_leg:             [42, 56],
  silly_point:           [58, 56],
  silly_mid_off:         [56, 52],
  silly_mid_on:          [44, 52],
  point:                 [72, 55],
  cover:                 [70, 45],
  extra_cover:           [66, 40],
  mid_off:               [56, 34],
  mid_on:                [44, 34],
  midwicket:             [30, 42],
  square_leg:            [28, 52],
  backward_point:        [72, 60],
  backward_square_leg:   [26, 60],
  short_third_man:       [70, 65],
  short_fine_leg:        [30, 65],
  third_man:             [80, 74],
  deep_point:            [88, 55],
  deep_cover:            [84, 40],
  long_off:              [58, 12],
  long_on:               [42, 12],
  deep_midwicket:        [14, 40],
  deep_square_leg:       [10, 55],
  fine_leg:              [20, 76],
  deep_backward_point:   [88, 62],
};


function FieldMapSVG({ elements }) {
  const mirror = elements?.batter_handedness === "left";
  const flipX = (x) => (mirror ? 100 - x : x);
  const fielders = (elements?.fielders || []).filter(
    (f) => f && FIELDER_COORDS[f.position],
  );
  return (
    <svg
      viewBox="0 0 100 100"
      className="w-full max-w-[320px] rounded-lg bg-emerald-950/40 border border-emerald-900/60"
      data-testid="visual-field-map-svg"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Boundary */}
      <circle cx="50" cy="50" r="48" fill="#0b3b25" stroke="#1e6b47" strokeWidth="0.6" />
      {/* Inner ring (30-yd) */}
      <circle cx="50" cy="50" r="28" fill="none" stroke="#1e6b47" strokeWidth="0.3" strokeDasharray="1 1" />
      {/* Pitch */}
      <rect x="47.5" y="42" width="5" height="22" fill="#c9a973" stroke="#8f7a4d" strokeWidth="0.2" />
      {/* Batter marker */}
      <circle cx={flipX(50)} cy="60" r="1.6" fill="#f59e0b" />
      {/* Bowler marker */}
      <circle cx="50" cy="44" r="1.4" fill="#93c5fd" />
      {/* Fielders */}
      {fielders.map((f, i) => {
        const [x, y] = FIELDER_COORDS[f.position];
        const color =
          f.role === "close_in" ? "#f87171" :
          f.role === "ring"     ? "#fbbf24" :
                                  "#34d399";
        return (
          <g key={`${f.position}-${i}`} data-testid={`fielder-${f.position}`}>
            <circle cx={flipX(x)} cy={y} r="1.8" fill={color} stroke="#0b0f14" strokeWidth="0.3" />
          </g>
        );
      })}
    </svg>
  );
}


// ---- TECHNIQUE DIAGRAM (Iter XX.235 — cricket-specific) --------------
// Renders a domain- and subtype-aware SVG scene with bat, ball, stumps,
// pitch, and shot direction arrows when the payload asks for them.
// Falls back to the XX.234 neutral stick figure when the payload has
// no XX.235 taxonomy fields (backward-compat).

// Shot-zone target coordinates on a 100×120 canvas (front view of the
// crease). x=50 is straight down the ground; low x = leg-side for RH.
const SHOT_ZONE_XY = {
  covers:      [82, 34],
  extra_cover: [80, 22],
  mid_off:     [62, 12],
  straight:    [50, 10],
  mid_on:      [38, 12],
  midwicket:   [20, 22],
  square_leg:  [12, 40],
  fine_leg:    [16, 78],
  third_man:   [86, 78],
  point:       [90, 46],
};

function StumpsSVG({ x = 50, y = 84 }) {
  return (
    <g data-testid="scene-stumps">
      <line x1={x - 3} y1={y - 8} x2={x - 3} y2={y} stroke="#e5e7eb" strokeWidth="0.6" />
      <line x1={x}     y1={y - 8} x2={x}     y2={y} stroke="#e5e7eb" strokeWidth="0.6" />
      <line x1={x + 3} y1={y - 8} x2={x + 3} y2={y} stroke="#e5e7eb" strokeWidth="0.6" />
      <line x1={x - 4} y1={y - 8} x2={x + 4} y2={y - 8} stroke="#e5e7eb" strokeWidth="0.4" />
    </g>
  );
}
function PitchSVG() {
  return (
    <rect x="46" y="60" width="8" height="52" fill="#c9a973" stroke="#8f7a4d"
           strokeWidth="0.2" opacity="0.7" data-testid="scene-pitch" />
  );
}
function BatSVG({ x, y, angle = -45 }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${angle})`} data-testid="scene-bat">
      <rect x="-1.2" y="-14" width="2.4" height="10" fill="#c9a973" stroke="#7c6437" strokeWidth="0.3" />
      <rect x="-0.6" y="-4"  width="1.2" height="4"  fill="#d1d5db" />
    </g>
  );
}
function BallSVG({ x, y, color = "#ef4444" }) {
  return (
    <circle cx={x} cy={y} r="1.6" fill={color} stroke="#7f1d1d"
             strokeWidth="0.2" data-testid="scene-ball" />
  );
}


function CricketTechniqueSVG({ elements, subtype, orientation }) {
  const domain    = elements?.cricket_domain || "batting";
  const shotZone  = elements?.shot_zone;
  const handMirror = elements?.handedness === "left";
  const flip = (x) => (handMirror ? 100 - x : x);
  const hasCurrent = !!elements?.current_arrow;
  const hasTarget  = !!elements?.target_arrow;

  // Iter XX.236 — larger figure + thicker strokes for phone readability.
  const S = { fig: 1.1, arrow: 1.6, ref: 0.5 };
  const isSide = (orientation || "").startsWith("side");
  const isTop  = orientation === "top_down";
  const playerX = 50;

  return (
    <svg viewBox="0 0 100 120"
         className="w-full max-w-[320px] rounded-lg bg-neutral-900 border border-neutral-800"
         data-testid="visual-technique-scene-svg"
         xmlns="http://www.w3.org/2000/svg">
      {/* Ground line */}
      <line x1="4" y1="112" x2="96" y2="112" stroke="#4b5563" strokeWidth="0.7" />

      {/* Reference target line — always present */}
      <line x1="50" y1="4" x2="50" y2="112" stroke="#22c55e"
             strokeWidth={S.ref} strokeDasharray="1.5 1.2"
             data-testid="reference-line-target" />

      {/* Pitch + stumps */}
      {(domain === "batting" || domain === "bowling") && (
        <rect x="44" y="56" width="12" height="56" fill="#c9a973"
               stroke="#8f7a4d" strokeWidth="0.25" opacity="0.72"
               data-testid="scene-pitch" />
      )}
      {(domain === "batting" || domain === "wicketkeeping") && (
        <g data-testid="scene-stumps-batter">
          <line x1="46.5" y1="86" x2="46.5" y2="96" stroke="#f3f4f6" strokeWidth="0.9" />
          <line x1="50"   y1="86" x2="50"   y2="96" stroke="#f3f4f6" strokeWidth="0.9" />
          <line x1="53.5" y1="86" x2="53.5" y2="96" stroke="#f3f4f6" strokeWidth="0.9" />
          <line x1="45"   y1="86" x2="55"   y2="86" stroke="#f3f4f6" strokeWidth="0.5" />
        </g>
      )}
      {domain === "bowling" && (
        <g data-testid="scene-stumps-bowler">
          <line x1="46.5" y1="10" x2="46.5" y2="20" stroke="#f3f4f6" strokeWidth="0.9" />
          <line x1="50"   y1="10" x2="50"   y2="20" stroke="#f3f4f6" strokeWidth="0.9" />
          <line x1="53.5" y1="10" x2="53.5" y2="20" stroke="#f3f4f6" strokeWidth="0.9" />
          <line x1="45"   y1="20" x2="55"   y2="20" stroke="#f3f4f6" strokeWidth="0.5" />
        </g>
      )}

      {/* Player figure — larger, orientation-aware */}
      {isTop ? (
        <g data-testid="scene-player-topdown">
          <circle cx="50" cy="58" r="10" fill="none" stroke="#e5e7eb" strokeWidth={S.fig} />
          <text x="50" y="62" fill="#93c5fd" fontSize="5" textAnchor="middle" fontWeight="600">wrist</text>
          <line x1="38" y1="58" x2="62" y2="58" stroke="#f59e0b"
                 strokeWidth="0.9" strokeDasharray="1.4 0.9" />
          <text x="63" y="59" fill="#f59e0b" fontSize="4.5">axis</text>
        </g>
      ) : isSide ? (
        <g data-testid="scene-player-side">
          {/* Head */}
          <circle cx={flip(40)} cy="42" r="5" fill="none" stroke="#e5e7eb" strokeWidth={S.fig} />
          {/* Neck + torso */}
          <line x1={flip(40)} y1="47" x2={flip(42)} y2="72" stroke="#e5e7eb" strokeWidth={S.fig} />
          {/* Front (striding) leg */}
          <line x1={flip(42)} y1="72" x2={flip(56)} y2="94" stroke="#e5e7eb" strokeWidth={S.fig} />
          {/* Back leg */}
          <line x1={flip(42)} y1="72" x2={flip(32)} y2="94" stroke="#e5e7eb" strokeWidth={S.fig} />
          {/* Shoulders */}
          <line x1={flip(40)} y1="52" x2={flip(46)} y2="52" stroke="#e5e7eb" strokeWidth={S.fig} />
          {/* Front arm (extended toward ball) */}
          <line x1={flip(46)} y1="52" x2={flip(58)} y2={domain === "batting" ? 76 : 44} stroke="#e5e7eb" strokeWidth={S.fig} />
          {/* Bat (batting) */}
          {domain === "batting" && (
            <g transform={`translate(${flip(58)} 76) rotate(${handMirror ? 50 : -50})`}>
              <rect x="-1.6" y="-18" width="3.2" height="14" fill="#c9a973" stroke="#7c6437" strokeWidth="0.4" />
              <rect x="-1"   y="-4"  width="2"   height="5"  fill="#d1d5db" />
            </g>
          )}
          {/* Ball at contact */}
          {domain === "batting" && (
            <g data-testid="scene-ball-contact">
              <circle cx={flip(62)} cy="82" r="2.2" fill="#ef4444" stroke="#7f1d1d" strokeWidth="0.4" />
              <circle cx={flip(62)} cy="82" r="3.6" fill="none" stroke="#fbbf24" strokeWidth="0.5" strokeDasharray="0.8 0.8" />
            </g>
          )}
          {/* Ball just released (bowling) */}
          {domain === "bowling" && (
            <circle cx={flip(60)} cy="38" r="2.2" fill="#ef4444" stroke="#7f1d1d" strokeWidth="0.4" />
          )}
        </g>
      ) : (
        <g data-testid="scene-player-front">
          <circle cx="50" cy="28" r="6" fill="none" stroke="#e5e7eb" strokeWidth={S.fig} />
          <line x1="50" y1="34" x2="50" y2="72" stroke="#e5e7eb" strokeWidth={S.fig} />
          {/* Shoulders wide */}
          <line x1="42" y1="40" x2="58" y2="40" stroke="#e5e7eb" strokeWidth={S.fig} />
          {/* Arms */}
          <line x1="42" y1="40" x2="34" y2="60" stroke="#e5e7eb" strokeWidth={S.fig} />
          <line x1="58" y1="40" x2="66" y2="60" stroke="#e5e7eb" strokeWidth={S.fig} />
          {/* Legs wide base */}
          <line x1="50" y1="72" x2="38" y2="94" stroke="#e5e7eb" strokeWidth={S.fig} />
          <line x1="50" y1="72" x2="62" y2="94" stroke="#e5e7eb" strokeWidth={S.fig} />
          {domain === "batting" && (
            <g transform="translate(38 68) rotate(-30)">
              <rect x="-1.6" y="-18" width="3.2" height="14" fill="#c9a973" stroke="#7c6437" strokeWidth="0.4" />
            </g>
          )}
          {(subtype === "catching_stance" || subtype === "keeping_stance") && (
            <g>
              <circle cx="50" cy="54" r="2.4" fill="#fbbf24" stroke="#92400e" strokeWidth="0.4" />
              <circle cx="50" cy="54" r="4"  fill="none"     stroke="#fbbf24" strokeWidth="0.4" strokeDasharray="0.8 0.8" />
            </g>
          )}
        </g>
      )}

      {/* Shot-direction arrow (batting) */}
      {shotZone && SHOT_ZONE_XY[shotZone] && (() => {
        const [tx, ty] = SHOT_ZONE_XY[shotZone];
        return (
          <g data-testid="arrow-shot-direction">
            <path d={`M ${flip(62)} 82 L ${flip(tx)} ${ty}`}
                  stroke="#fbbf24" strokeWidth={S.arrow} fill="none"
                  markerEnd="url(#arrow-amber)" />
            <text x={flip(tx) + (handMirror ? -14 : 3)} y={ty - 2}
                  fill="#fbbf24" fontSize="4.4" fontWeight="600">
              {(shotZone || "").replace(/_/g, " ")}
            </text>
          </g>
        );
      })()}

      {/* Current arrow (only rendered on high confidence) */}
      {hasCurrent && (
        <g data-testid="visual-arrow-current">
          <path d="M40 22 L20 30" stroke="#ef4444" strokeWidth={S.arrow}
                 fill="none" markerEnd="url(#arrow-red)" />
          <text x="4" y="30" fill="#ef4444" fontSize="4.4" fontWeight="600">current</text>
        </g>
      )}
      {/* Target arrow (high + provisional) */}
      {hasTarget && (
        <g data-testid="visual-arrow-target">
          <path d="M40 22 L40 6" stroke="#22c55e" strokeWidth={S.arrow}
                 fill="none" markerEnd="url(#arrow-green)" />
          <text x="42" y="10" fill="#22c55e" fontSize="4.4" fontWeight="600">target</text>
        </g>
      )}

      <defs>
        <marker id="arrow-red"   viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0 0 L10 5 L0 10 z" fill="#ef4444" />
        </marker>
        <marker id="arrow-green" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0 0 L10 5 L0 10 z" fill="#22c55e" />
        </marker>
        <marker id="arrow-amber" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0 0 L10 5 L0 10 z" fill="#fbbf24" />
        </marker>
      </defs>
    </svg>
  );
}


// XX.234 stick-figure kept for pre-XX.235 payloads that don't carry
// the new taxonomy (backward-compat).
function TechniqueDiagramSVG({ elements }) {
  const hasCurrent = !!elements?.current_arrow;
  const hasTarget  = !!elements?.target_arrow;
  return (
    <svg
      viewBox="0 0 100 120"
      className="w-full max-w-[240px] rounded-lg bg-neutral-900 border border-neutral-800"
      data-testid="visual-technique-diagram-svg"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Ground line */}
      <line x1="10" y1="112" x2="90" y2="112" stroke="#4b5563" strokeWidth="0.6" />
      {/* Target line (reference — always shown) */}
      <line x1="50" y1="8" x2="50" y2="112" stroke="#22c55e"
             strokeWidth="0.4" strokeDasharray="1 1" data-testid="reference-line-target" />
      {/* Stick figure — head */}
      <circle cx="50" cy="18" r="6" fill="none" stroke="#e5e7eb" strokeWidth="0.8" />
      {/* Torso */}
      <line x1="50" y1="24" x2="50" y2="68" stroke="#e5e7eb" strokeWidth="0.8" />
      {/* Arms */}
      <line x1="50" y1="34" x2="34" y2="52" stroke="#e5e7eb" strokeWidth="0.8" />
      <line x1="50" y1="34" x2="66" y2="52" stroke="#e5e7eb" strokeWidth="0.8" />
      {/* Legs */}
      <line x1="50" y1="68" x2="40" y2="108" stroke="#e5e7eb" strokeWidth="0.8" />
      <line x1="50" y1="68" x2="60" y2="108" stroke="#e5e7eb" strokeWidth="0.8" />
      {/* Current arrow (high confidence only) */}
      {hasCurrent && (
        <g data-testid="visual-arrow-current">
          <path d="M50 18 L26 24" stroke="#ef4444" strokeWidth="1.2"
                 fill="none" markerEnd="url(#arrow-red)" />
          <text x="12" y="24" fill="#ef4444" fontSize="4">current</text>
        </g>
      )}
      {/* Target arrow (high + provisional) */}
      {hasTarget && (
        <g data-testid="visual-arrow-target">
          <path d="M50 18 L50 6" stroke="#22c55e" strokeWidth="1.2"
                 fill="none" markerEnd="url(#arrow-green)" />
          <text x="52" y="8" fill="#22c55e" fontSize="4">target</text>
        </g>
      )}
      <defs>
        <marker id="arrow-red" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4" markerHeight="4" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#ef4444" />
        </marker>
        <marker id="arrow-green" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4" markerHeight="4" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#22c55e" />
        </marker>
      </defs>
    </svg>
  );
}


// ---- ACTION PLAN (6-ball strip) ----------------------------------------
function ActionPlanStrip({ elements }) {
  const sequence = elements?.sequence || [];
  const intentColor = {
    leave:      "bg-neutral-800 text-neutral-400",
    defend:     "bg-neutral-800 text-neutral-300",
    block:      "bg-neutral-800 text-neutral-300",
    single:     "bg-emerald-900/50 text-emerald-300",
    rotate:     "bg-emerald-900/50 text-emerald-300",
    attack:     "bg-amber-900/50 text-amber-300",
    boundary:   "bg-amber-900/50 text-amber-300",
    safe_zone:  "bg-neutral-800 text-neutral-400",
  };
  return (
    <div data-testid="visual-action-plan-strip" className="w-full max-w-[320px]">
      <div className="grid grid-cols-6 gap-1">
        {sequence.map((s, i) => (
          <div
            key={i}
            data-testid={`ball-slot-${i}`}
            className={`h-9 rounded flex items-center justify-center text-[10px] uppercase tracking-wide ${intentColor[s] || "bg-neutral-800 text-neutral-500"}`}
          >
            {s.replace(/_/g, " ")}
          </div>
        ))}
      </div>
      {elements?.cue && (
        <p className="mt-2 text-xs text-neutral-300">
          <span className="text-neutral-500">Cue: </span>{elements.cue}
        </p>
      )}
      {elements?.report_back && (
        <p className="mt-1 text-[11px] text-neutral-500 italic">
          {elements.report_back}
        </p>
      )}
    </div>
  );
}


// ==========================================================================
// Iter XX.239 · Wrong-vs-Right Coaching Visuals
// Iter XX.239a · Cricket Pose Realism Polish — redraws the batter, bowler
// and wrist-grip poses with recognizable human anatomy (torso, upper &
// lower arms, thighs, shins, elbows, knees, shoes, pads, helmet+grille,
// gloves, proportioned bat). Same cricket action in both panels — only
// the coaching variable (head position, shoulder rotation, gather
// direction, bat path) changes so the isolated variable is obvious.
// Cricket action must be recognizable from the illustration alone.
// ==========================================================================

// Batter — realistic side-on drive / cover-drive / defense / pull pose.
// `mode`: drive-head-falls | drive-head-over | cover-head-falls |
//         cover-head-over-knee | across-line | straight-bat |
//         defense-hard-hands | defense-soft-under-eyes |
//         pull-outside-line | pull-inside-line
function BatterPose({ mode, tint }) {
  const guide       = tint === "error" ? "#ef4444" : "#22c55e";
  const guideDash   = tint === "error" ? "5 3" : "0";
  const skin        = "#f5cf9b";
  const shirt       = "#e8ecf1";
  const shirtLine   = "#94a3b8";
  const pad         = "#f8fafc";
  const padStrap    = "#94a3b8";
  const bat         = "#d4a274";
  const batDark     = "#7c5433";
  const glove       = "#1f2937";
  const shoe        = "#0f172a";
  const helmet      = "#1e3a8a";
  const helmetHi    = "#3b5bdb";
  const grille      = "#111827";

  const headFalls   = /head-falls|hard-hands|across-line|outside-line/.test(mode);
  const acrossLine  = mode === "across-line";

  // Same cover-drive action in both panels — only these vary:
  const headCx        = headFalls ? 62 : 56;
  const headCy        = headFalls ? 32 : 22;
  const headTilt      = headFalls ? 18 : 0;
  const frontShoulderOpen = headFalls ? 3 : 0;
  const batTipX = acrossLine ? 88 : 78;
  const batTipY = acrossLine ? 74 : 96;
  const ballCx  = acrossLine ? 84 : 72;
  const ballCy  = acrossLine ? 78 : 92;

  return (
    <svg viewBox="0 0 100 130" width="100%" height="100%"
         preserveAspectRatio="xMidYMid meet" className="block">
      <rect x="0" y="114" width="100" height="16" fill="#7a5931" />
      <line x1="0" y1="114" x2="100" y2="114" stroke="#3d2c19" strokeWidth="0.6" />
      <line x1="0" y1="122" x2="100" y2="122" stroke="#5b4022" strokeWidth="0.3" />

      {/* STUMPS behind batter */}
      <g transform="translate(12, 82)">
        <rect x="0"    y="0" width="1.6" height="32" fill="#c69364" />
        <rect x="3.5"  y="0" width="1.6" height="32" fill="#c69364" />
        <rect x="7"    y="0" width="1.6" height="32" fill="#c69364" />
        <line x1="-0.4" y1="0" x2="9" y2="0" stroke="#5b4022" strokeWidth="1.2" />
      </g>

      {/* BACK LEG — thigh + shin + shoe (pad-covered) */}
      <path d="M 43 53 L 47 53 L 42 80 L 38 78 Z" fill={pad} stroke={padStrap} strokeWidth="0.5"/>
      <line x1="39" y1="63" x2="46.5" y2="65" stroke={padStrap} strokeWidth="0.4"/>
      <line x1="38.6" y1="72" x2="45" y2="74" stroke={padStrap} strokeWidth="0.4"/>
      <path d="M 38 78 L 42 80 L 40 110 L 34 110 Z" fill={pad} stroke={padStrap} strokeWidth="0.5"/>
      <line x1="35" y1="90" x2="41.5" y2="92" stroke={padStrap} strokeWidth="0.4"/>
      <line x1="35" y1="100" x2="41" y2="102" stroke={padStrap} strokeWidth="0.4"/>
      <ellipse cx="37" cy="113" rx="6.5" ry="2.6" fill={shoe} />
      <ellipse cx="37" cy="112.4" rx="6.5" ry="0.8" fill="#334155" />

      {/* FRONT LEG — steps forward, knee bent, foot planted */}
      <path d="M 51 53 L 56 53 L 66 75 L 60 77 Z" fill={pad} stroke={padStrap} strokeWidth="0.5"/>
      <line x1="55" y1="61" x2="63" y2="66" stroke={padStrap} strokeWidth="0.4"/>
      <line x1="57" y1="69" x2="65" y2="74" stroke={padStrap} strokeWidth="0.4"/>
      <path d="M 60 77 L 66 75 L 72 108 L 64 110 Z" fill={pad} stroke={padStrap} strokeWidth="0.5"/>
      <line x1="62" y1="87" x2="69" y2="90" stroke={padStrap} strokeWidth="0.4"/>
      <line x1="63" y1="97" x2="70" y2="100" stroke={padStrap} strokeWidth="0.4"/>
      <ellipse cx="70" cy="113" rx="7.5" ry="2.9" fill={shoe} />
      <ellipse cx="70" cy="112.4" rx="7.5" ry="0.8" fill="#334155" />

      {/* TORSO */}
      <path d={`M 42 32 L ${55 + frontShoulderOpen} 32 L ${52 + frontShoulderOpen} 54 L 44 54 Z`}
            fill={shirt} stroke={shirtLine} strokeWidth="0.5" />
      <path d={`M 46 32 Q 48 30 ${52 + frontShoulderOpen} 32`}
            fill="none" stroke={shirtLine} strokeWidth="0.5"/>
      <path d="M 46 34 Q 48 42 46 52" stroke={shirtLine} strokeWidth="0.3" fill="none" opacity="0.6"/>

      {/* BACK ARM — from back shoulder, elbow tucked, to bottom-hand */}
      <path d="M 42 33 L 46 33 L 58 46 L 55 48 Z" fill={skin}/>
      <path d="M 55 48 L 58 46 L 56 60 L 52 60 Z" fill={skin}/>
      <ellipse cx="54" cy="60" rx="3.2" ry="2.6" fill={glove}/>
      <rect x="52" y="59" width="4" height="0.6" fill="#4b5563"/>

      {/* FRONT ARM — from front shoulder, elbow high, to top-hand */}
      <path d={`M ${53 + frontShoulderOpen} 33 L ${56 + frontShoulderOpen} 33
                 L 52 46 L 49 46 Z`} fill={skin}/>
      <path d="M 49 46 L 52 46 L 50 58 L 47 58 Z" fill={skin}/>
      <ellipse cx="48.5" cy="58" rx="3.2" ry="2.6" fill={glove}/>
      <rect x="46.5" y="57" width="4" height="0.6" fill="#4b5563"/>

      {/* BAT — handle + rubber grip + blade */}
      <g>
        <line x1="50" y1="58" x2="55" y2="66"
              stroke={batDark} strokeWidth="2.8" strokeLinecap="round"/>
        <line x1="51" y1="60" x2="53" y2="61" stroke="#4b3520" strokeWidth="0.5"/>
        <line x1="52" y1="62" x2="54" y2="63" stroke="#4b3520" strokeWidth="0.5"/>
        <path d={`M 53 66 L 57 66 L ${batTipX + 2.5} ${batTipY} L ${batTipX - 2.5} ${batTipY} Z`}
              fill={bat} stroke={batDark} strokeWidth="0.6"/>
        <line x1="55" y1="66" x2={batTipX} y2={batTipY}
              stroke={batDark} strokeWidth="0.5" opacity="0.7"/>
      </g>

      {/* BALL near contact */}
      <circle cx={ballCx} cy={ballCy} r="3.4" fill="#dc2626" stroke="#7f1d1d" strokeWidth="0.5"/>
      <path d={`M ${ballCx - 3} ${ballCy} Q ${ballCx} ${ballCy + 1.2} ${ballCx + 3} ${ballCy}`}
            stroke="#fef3c7" strokeWidth="0.5" fill="none"/>

      {/* NECK */}
      <path d="M 47 28 L 51 28 L 50 33 L 48 33 Z" fill={skin}/>

      {/* HELMET + FACE + GRILLE */}
      <g transform={`translate(${headCx}, ${headCy}) rotate(${headTilt})`}>
        <path d="M -7 -2 Q -7 -9 0 -9 Q 7 -9 7 -2 L 7 4 L -7 4 Z"
              fill={helmet} stroke="#0f172a" strokeWidth="0.5"/>
        <path d="M -5 -6 Q -1 -8 3 -6" fill="none" stroke={helmetHi} strokeWidth="0.6" opacity="0.7"/>
        <path d="M -7 -2 L -9 1 L 7 1 L 7 -2 Z" fill={helmet} stroke="#0f172a" strokeWidth="0.5"/>
        <rect x="-5" y="1" width="10" height="6" fill={skin}/>
        <g stroke={grille} strokeWidth="0.6">
          <line x1="-5" y1="2.5" x2="5" y2="2.5"/>
          <line x1="-5" y1="4.5" x2="5" y2="4.5"/>
          <line x1="-5" y1="6.5" x2="5" y2="6.5"/>
          <line x1="-3" y1="1" x2="-3" y2="7"/>
          <line x1="0"  y1="1" x2="0"  y2="7"/>
          <line x1="3"  y1="1" x2="3"  y2="7"/>
        </g>
      </g>

      {/* ALIGNMENT GUIDE */}
      {headFalls ? (
        <line x1={headCx - 1} y1={headCy - 8} x2="80" y2="113"
              stroke={guide} strokeWidth="2" strokeDasharray={guideDash} opacity="0.95"/>
      ) : (
        <line x1={headCx} y1={headCy - 8} x2={headCx + 6} y2="113"
              stroke={guide} strokeWidth="2" opacity="0.95"/>
      )}
    </svg>
  );
}

// Bowler — realistic side-on. Two variants: release-point and gather.
function BowlerPose({ mode, tint }) {
  const guide       = tint === "error" ? "#ef4444" : "#22c55e";
  const guideDash   = tint === "error" ? "5 3" : "0";
  const skin        = "#f5cf9b";
  const shirt       = "#e8ecf1";
  const shirtLine   = "#94a3b8";
  const shirtDark   = "#cbd5e1";
  const shoe        = "#0f172a";
  const ball        = "#dc2626";
  const ballSeam    = "#fef3c7";
  const cap         = "#1e3a8a";

  const gatherVertical = mode === "gather-jumps-vertical";
  const gatherForward  = mode === "gather-drives-forward";
  const headFallsOff   = mode === "head-falls-off-side";

  if (gatherVertical || gatherForward) {
    const bodyLeanX = gatherForward ? 14 : 0;
    const bodyLift  = gatherVertical ? -6 : -2;   // both gather variants are airborne
    const forwardX  = gatherForward ? 8 : 0;
    const cx        = 42 + forwardX;
    const headCx    = cx + bodyLeanX / 2;
    const headCy    = 20 + bodyLift;
    // shift both feet upward — bowler is airborne in the gather phase
    const backFootY  = 92;
    const driveKneeY = 48;                        // knee is raised high (up near hip)
    return (
      <svg viewBox="0 0 100 130" width="100%" height="100%"
           preserveAspectRatio="xMidYMid meet" className="block">
        <rect x="0" y="114" width="100" height="16" fill="#7a5931" />
        <line x1="0" y1="114" x2="100" y2="114" stroke="#3d2c19" strokeWidth="0.6" />
        <g transform="translate(80, 82)">
          <rect x="0"   y="0" width="1.4" height="32" fill="#c69364" />
          <rect x="3"   y="0" width="1.4" height="32" fill="#c69364" />
          <rect x="6"   y="0" width="1.4" height="32" fill="#c69364" />
          <line x1="-0.4" y1="0" x2="7.5" y2="0" stroke="#5b4022" strokeWidth="1.2" />
        </g>

        {/* BACK LEG — trailing, extended straight behind, foot pointing down */}
        <path d={`M ${cx - 4} 55 L ${cx} 55 L ${cx - 12} 78 L ${cx - 16} 76 Z`}
              fill={shirtDark} stroke={shirtLine} strokeWidth="0.4"/>
        <path d={`M ${cx - 16} 76 L ${cx - 12} 78 L ${cx - 20} ${backFootY} L ${cx - 24} ${backFootY - 2} Z`}
              fill={skin}/>
        <ellipse cx={cx - 22} cy={backFootY + 2} rx="6" ry="2.4" fill={shoe}/>

        {/* FRONT / DRIVE LEG — thigh horizontal (knee raised to hip level) */}
        <path d={`M ${cx + 2} 55 L ${cx + 6} 55 L ${cx + 22} 53 L ${cx + 22} 58 Z`}
              fill={shirtDark} stroke={shirtLine} strokeWidth="0.4"/>
        {/* shin — hanging down from raised knee */}
        <path d={`M ${cx + 20} 55 L ${cx + 24} 55 L ${cx + 26} ${driveKneeY + 24} L ${cx + 22} ${driveKneeY + 26} Z`}
              fill={skin}/>
        <ellipse cx={cx + 24} cy={driveKneeY + 28} rx="5.5" ry="2.2" fill={shoe}/>

        {/* TORSO — leans forward for correct, upright for vertical */}
        <path d={`M ${cx - 6} 33 L ${cx + 6 + bodyLeanX / 2} 33
                   L ${cx + 5 + bodyLeanX} 55 L ${cx - 4 + bodyLeanX} 55 Z`}
              fill={shirt} stroke={shirtLine} strokeWidth="0.5"/>

        {/* FRONT (non-bowling) arm — pumped forward for momentum */}
        <path d={`M ${cx - 5} 34 L ${cx - 2} 34 L ${cx - 14} 26 L ${cx - 17} 28 Z`}
              fill={skin}/>
        <path d={`M ${cx - 17} 28 L ${cx - 14} 26 L ${cx - 22} 18 L ${cx - 25} 20 Z`}
              fill={skin}/>

        {/* BOWLING arm — up, holding ball at chest / above head */}
        <path d={`M ${cx + 4} 34 L ${cx + 7 + bodyLeanX / 2} 34
                   L ${cx + 14 + bodyLeanX} 22 L ${cx + 11 + bodyLeanX} 20 Z`}
              fill={skin}/>
        <path d={`M ${cx + 11 + bodyLeanX} 20 L ${cx + 14 + bodyLeanX} 22
                   L ${cx + 10 + bodyLeanX} 10 L ${cx + 7 + bodyLeanX} 12 Z`}
              fill={skin}/>
        <circle cx={cx + 9 + bodyLeanX} cy="8" r="3" fill={ball} stroke="#7f1d1d" strokeWidth="0.4"/>
        <path d={`M ${cx + 6 + bodyLeanX} 8 Q ${cx + 9 + bodyLeanX} 10 ${cx + 12 + bodyLeanX} 8`}
              stroke={ballSeam} strokeWidth="0.4" fill="none"/>

        {/* NECK */}
        <path d={`M ${cx - 1} 28 L ${cx + 3} 28 L ${cx + 2} 33 L ${cx} 33 Z`} fill={skin}/>

        {/* HEAD + CAP */}
        <g transform={`translate(${headCx}, ${headCy})`}>
          <path d="M -6 -2 Q -6 -8 0 -8 Q 6 -8 6 -2 L 6 6 L -6 6 Z"
                fill={skin} stroke="#0f172a" strokeWidth="0.5"/>
          <path d="M -6.5 -3 Q 0 -9 6.5 -3 L 6.5 -1 L -6.5 -1 Z" fill={cap} stroke="#0f172a" strokeWidth="0.4"/>
          <path d="M -6.5 -1 L -10 1 L 6.5 -1 Z" fill={cap} stroke="#0f172a" strokeWidth="0.4"/>
          <ellipse cx="3" cy="1" rx="0.6" ry="0.9" fill="#0f172a"/>
        </g>

        {/* DIRECTION ARROW */}
        {gatherVertical ? (
          <line x1={cx + 2} y1="2" x2={cx + 2} y2="72"
                stroke={guide} strokeWidth="2.4" strokeDasharray={guideDash}
                markerEnd="url(#arr-xx239a-v)"/>
        ) : (
          <line x1={cx - 12} y1={headCy + 10} x2="94" y2={headCy + 22}
                stroke={guide} strokeWidth="2.4" markerEnd="url(#arr-xx239a-h)"/>
        )}
        <defs>
          <marker id="arr-xx239a-v" viewBox="0 0 8 8" refX="6" refY="4"
                  markerWidth="5" markerHeight="5" orient="auto">
            <path d="M 0 0 L 8 4 L 0 8 z" fill={guide}/>
          </marker>
          <marker id="arr-xx239a-h" viewBox="0 0 8 8" refX="6" refY="4"
                  markerWidth="5" markerHeight="5" orient="auto">
            <path d="M 0 0 L 8 4 L 0 8 z" fill={guide}/>
          </marker>
        </defs>
      </svg>
    );
  }

  // ---------------- RELEASE POINT ----------------
  const bodyLean = headFallsOff ? 10 : 0;
  const headCx   = 46 - bodyLean;
  return (
    <svg viewBox="0 0 100 130" width="100%" height="100%"
         preserveAspectRatio="xMidYMid meet" className="block">
      <rect x="0" y="114" width="100" height="16" fill="#7a5931" />
      <line x1="0" y1="114" x2="100" y2="114" stroke="#3d2c19" strokeWidth="0.6" />
      <g transform="translate(80, 82)">
        <rect x="0"   y="0" width="1.4" height="32" fill="#c69364" />
        <rect x="3"   y="0" width="1.4" height="32" fill="#c69364" />
        <rect x="6"   y="0" width="1.4" height="32" fill="#c69364" />
        <line x1="-0.4" y1="0" x2="7.5" y2="0" stroke="#5b4022" strokeWidth="1.2" />
      </g>

      {/* BACK LEG (trailing) */}
      <path d="M 44 54 L 48 54 L 40 88 L 34 86 Z" fill={shirtDark} stroke={shirtLine} strokeWidth="0.4"/>
      <path d="M 34 86 L 40 88 L 34 112 L 28 111 Z" fill={skin}/>
      <ellipse cx="30" cy="113" rx="6" ry="2.4" fill={shoe}/>

      {/* FRONT LEG (braced) */}
      <path d="M 52 54 L 56 54 L 62 82 L 56 84 Z" fill={shirtDark} stroke={shirtLine} strokeWidth="0.4"/>
      <path d="M 56 84 L 62 82 L 66 112 L 60 112 Z" fill={skin}/>
      <ellipse cx="63" cy="113" rx="6.6" ry="2.6" fill={shoe}/>

      {/* TORSO */}
      <path d={`M ${44 - bodyLean} 32 L ${56 - bodyLean} 32 L 54 54 L 44 54 Z`}
            fill={shirt} stroke={shirtLine} strokeWidth="0.5"/>

      {/* FRONT arm — pulled down (correct) or collapsed across (error) */}
      <path d={`M ${44 - bodyLean} 33 L ${47 - bodyLean} 33
                 L ${headFallsOff ? 28 : 42} 48 L ${headFallsOff ? 25 : 39} 50 Z`} fill={skin}/>
      <path d={`M ${headFallsOff ? 25 : 39} 50 L ${headFallsOff ? 28 : 42} 48
                 L ${headFallsOff ? 24 : 40} 64 L ${headFallsOff ? 21 : 37} 62 Z`} fill={skin}/>

      {/* BOWLING arm — high at release */}
      <path d={`M ${54 - bodyLean} 32 L ${57 - bodyLean} 32
                 L ${56 - bodyLean} 20 L ${52 - bodyLean} 20 Z`} fill={skin}/>
      <path d={`M ${52 - bodyLean} 20 L ${56 - bodyLean} 20
                 L ${60 - bodyLean} 10 L ${56 - bodyLean} 8 Z`} fill={skin}/>
      <circle cx={62 - bodyLean} cy="6" r="3" fill={ball} stroke="#7f1d1d" strokeWidth="0.4"/>
      <path d={`M ${59 - bodyLean} 6 Q ${62 - bodyLean} 8 ${65 - bodyLean} 6`}
            stroke={ballSeam} strokeWidth="0.4" fill="none"/>

      {/* NECK */}
      <path d={`M ${47 - bodyLean} 28 L ${51 - bodyLean} 28
                 L ${50 - bodyLean} 33 L ${48 - bodyLean} 33 Z`} fill={skin}/>

      {/* HEAD + CAP */}
      <g transform={`translate(${headCx}, 22) rotate(${headFallsOff ? -14 : 0})`}>
        <path d="M -6 -2 Q -6 -8 0 -8 Q 6 -8 6 -2 L 6 6 L -6 6 Z"
              fill={skin} stroke="#0f172a" strokeWidth="0.5"/>
        <path d="M -6.5 -3 Q 0 -9 6.5 -3 L 6.5 -1 L -6.5 -1 Z" fill={cap} stroke="#0f172a" strokeWidth="0.4"/>
        <path d="M -6.5 -1 L -10 1 L 6.5 -1 Z" fill={cap} stroke="#0f172a" strokeWidth="0.4"/>
        <ellipse cx="3" cy="1" rx="0.6" ry="0.9" fill="#0f172a"/>
      </g>

      {/* ALIGNMENT GUIDE */}
      {headFallsOff ? (
        <line x1={headCx} y1="10" x2={headCx - 6} y2="70"
              stroke={guide} strokeWidth="2" strokeDasharray={guideDash} opacity="0.95"/>
      ) : (
        <line x1="46" y1="10" x2="46" y2="112"
              stroke={guide} strokeWidth="2" opacity="0.95"/>
      )}
    </svg>
  );
}

// Wrist / grip — leg-break grip with cocked wrist. Close-up.
function WristGripPose() {
  const skin      = "#f5cf9b";
  const skinDark  = "#c99a63";
  const guide     = "#22c55e";
  return (
    <svg viewBox="0 0 100 130" width="100%" height="100%"
         preserveAspectRatio="xMidYMid meet" className="block">
      {/* forearm — up from bottom-left */}
      <path d="M 10 118 L 22 118 L 46 62 L 34 58 Z" fill={skin} stroke={skinDark} strokeWidth="0.5"/>
      {/* wristband */}
      <path d="M 32 60 L 46 62 L 42 74 L 28 72 Z" fill="#1e40af" stroke="#0f172a" strokeWidth="0.3"/>

      {/* PALM — cocked back */}
      <path d="M 34 58 Q 28 46 40 36 Q 56 30 64 42 Q 64 56 52 60 Z"
            fill={skin} stroke={skinDark} strokeWidth="0.6"/>
      {/* palm crease shading */}
      <path d="M 40 50 Q 48 52 56 48" stroke={skinDark} strokeWidth="0.3" fill="none" opacity="0.6"/>

      {/* THUMB wrapping around ball */}
      <path d="M 38 42 Q 30 42 34 32 Q 40 30 44 36 Q 46 42 42 44 Z"
            fill={skin} stroke={skinDark} strokeWidth="0.5"/>

      {/* FINGERS 1-4 gripping ball from top */}
      <g stroke={skinDark} strokeWidth="0.5" fill={skin}>
        <path d="M 44 38 Q 46 22 54 22 Q 58 24 56 40 Z"/>
        <path d="M 54 36 Q 56 18 64 18 Q 68 20 66 38 Z"/>
        <path d="M 62 38 Q 66 22 72 24 Q 74 30 70 42 Z"/>
        <path d="M 68 42 Q 74 32 78 36 Q 78 42 74 46 Z"/>
      </g>

      {/* BALL on top of grip */}
      <circle cx="60" cy="42" r="12" fill="#dc2626" stroke="#7f1d1d" strokeWidth="0.6"/>
      <path d="M 49 40 Q 60 47 71 40" stroke="#fef3c7" strokeWidth="1.4" fill="none"/>
      <g stroke="#fef3c7" strokeWidth="0.5">
        <line x1="52" y1="41" x2="53" y2="43"/>
        <line x1="55" y1="43" x2="56" y2="45"/>
        <line x1="60" y1="44" x2="61" y2="46"/>
        <line x1="65" y1="43" x2="66" y2="45"/>
        <line x1="68" y1="41" x2="69" y2="43"/>
      </g>
      <ellipse cx="55" cy="36" rx="3" ry="1.6" fill="#f87171" opacity="0.5"/>

      {/* WRIST-COCK GUIDE — curved arrow */}
      <path d="M 30 100 Q 20 70 40 50" stroke={guide} strokeWidth="2.4"
            fill="none" markerEnd="url(#arr-wrist-xx239a)"/>
      <defs>
        <marker id="arr-wrist-xx239a" viewBox="0 0 8 8" refX="6" refY="4"
                markerWidth="5" markerHeight="5" orient="auto">
          <path d="M 0 0 L 8 4 L 0 8 z" fill={guide}/>
        </marker>
      </defs>
      {/* release snap direction */}
      <path d="M 70 42 Q 82 46 84 60" stroke={guide} strokeWidth="1.6"
            strokeDasharray="3 2" fill="none" markerEnd="url(#arr-wrist-xx239a)"/>

      {/* label: third finger emphasis */}
      <circle cx="60" cy="20" r="1.8" fill={guide}/>
    </svg>
  );
}

// Pose dispatcher.
function PoseForKey({ poseKey, tint }) {
  const map = {
    "batter_drive_head_falls":         [BatterPose, "drive-head-falls"],
    "batter_drive_head_over_ball":     [BatterPose, "drive-head-over"],
    "batter_cover_head_falls":         [BatterPose, "cover-head-falls"],
    "batter_cover_head_over_knee":     [BatterPose, "cover-head-over-knee"],
    "batter_defense_hard_hands":       [BatterPose, "defense-hard-hands"],
    "batter_defense_soft_under_eyes":  [BatterPose, "defense-soft-under-eyes"],
    "batter_pull_outside_line":        [BatterPose, "pull-outside-line"],
    "batter_pull_inside_line":         [BatterPose, "pull-inside-line"],
    "batter_across_line":              [BatterPose, "across-line"],
    "batter_straight_bat":             [BatterPose, "straight-bat"],
    "bowler_head_falls_off_side":      [BowlerPose, "head-falls-off-side"],
    "bowler_stays_tall":               [BowlerPose, "stays-tall"],
    "bowler_gather_jumps_vertical":    [BowlerPose, "gather-jumps-vertical"],
    "bowler_gather_drives_forward":    [BowlerPose, "gather-drives-forward"],
    "leg_break_wrist_grip":            [WristGripPose, null],
    "off_break_finger_grip":           [WristGripPose, null],
    "seam_upright_grip":               [WristGripPose, null],
  };
  const entry = map[poseKey];
  if (!entry) return null;
  const [Comp, mode] = entry;
  return <Comp mode={mode} tint={tint} />;
}

// Coaching panel — pose dominates, ONE callout, no header text (chip only).
function CoachingPanel({ pose, tint, callouts }) {
  const border   = tint === "error" ? "border-rose-900/60" : "border-emerald-900/60";
  const bg       = tint === "error" ? "bg-rose-950/25"     : "bg-emerald-950/25";
  const chipBg   = tint === "error" ? "bg-rose-500"        : "bg-emerald-500";
  const label    = tint === "error" ? "COMMON MISTAKE"     : "CORRECT TECHNIQUE";
  const oneCall  = (callouts || [])[0] || "";
  return (
    <div className={`rounded-xl border ${border} ${bg} p-2 flex flex-col gap-1.5`}
         data-testid={`xx239-panel-${tint}`}>
      <div className="flex items-center gap-1.5">
        <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${chipBg} text-white text-[11px] font-bold`}>
          {tint === "error" ? "✕" : "✓"}
        </span>
        <span className={`text-[9px] font-bold tracking-wider ${tint === "error" ? "text-rose-400" : "text-emerald-400"}`}>
          {label}
        </span>
      </div>
      <div className="w-full bg-neutral-950/70 rounded-lg overflow-hidden"
           style={{ aspectRatio: "100 / 130" }}>
        <PoseForKey poseKey={pose} tint={tint} />
      </div>
      {oneCall && (
        <p className="text-[11px] leading-snug text-neutral-300"
           data-testid={`xx239-callout-${tint}-0`}>
          <span className={tint === "error" ? "text-rose-400" : "text-emerald-400"}>
            {tint === "error" ? "✕ " : "✓ "}
          </span>
          {oneCall}
        </p>
      )}
    </div>
  );
}

function CricketWrongVsRightSVG({ elements }) {
  const {
    error_pose, correct_pose,
    error_callouts = [], correct_callouts = [],
    key_cue,
  } = elements || {};
  return (
    <div className="space-y-2" data-testid="xx239-wrong-vs-right">
      <div className="grid grid-cols-2 gap-2">
        <CoachingPanel pose={error_pose}   tint="error"   callouts={error_callouts}/>
        <CoachingPanel pose={correct_pose} tint="correct" callouts={correct_callouts}/>
      </div>
      {key_cue && (
        <div className="rounded-xl border border-emerald-900/60 bg-emerald-950/25 p-2.5 flex gap-2.5 items-center"
             data-testid="xx239-key-cue">
          <span className="text-base leading-none">💡</span>
          <div className="flex flex-col leading-tight">
            <span className="text-[9px] font-bold tracking-wider text-emerald-400">KEY CUE</span>
            <span className="text-sm font-semibold text-neutral-100">
              &ldquo;{key_cue}&rdquo;
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function CricketSingleAnnotatedSVG({ elements }) {
  const { single_pose, annotations = [], key_cue } = elements || {};
  const oneAnn = annotations[0] || "";
  return (
    <div className="space-y-2" data-testid="xx239-single-annotated">
      <div className="rounded-xl border border-emerald-900/60 bg-emerald-950/25 p-2 flex flex-col gap-1.5">
        <div className="w-full bg-neutral-950/70 rounded-lg overflow-hidden"
             style={{ aspectRatio: "100 / 130" }}>
          <PoseForKey poseKey={single_pose} tint="correct" />
        </div>
        {oneAnn && (
          <p className="text-[11px] leading-snug text-neutral-300"
             data-testid="xx239-annotation-0">
            <span className="text-emerald-400">→ </span>{oneAnn}
          </p>
        )}
      </div>
      {key_cue && (
        <div className="rounded-xl border border-emerald-900/60 bg-emerald-950/25 p-2.5 flex gap-2.5 items-center"
             data-testid="xx239-key-cue">
          <span className="text-base leading-none">💡</span>
          <div className="flex flex-col leading-tight">
            <span className="text-[9px] font-bold tracking-wider text-emerald-400">KEY CUE</span>
            <span className="text-sm font-semibold text-neutral-100">
              &ldquo;{key_cue}&rdquo;
            </span>
          </div>
        </div>
      )}
    </div>
  );
}


// ---- Engagement beacon hook -------------------------------------------
function useEngagementBeacon(payload) {
  const mountedAt = useRef(Date.now());
  useEffect(() => {
    if (!payload) return;
    _emit("visual_explanation_engaged", {
      trigger_reason: payload.type,
      cta_variant:    payload.confidence,
      platform:       "web",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);   // fires once when the visual first appears in the DOM
  return mountedAt;
}


// ---- Top-level renderer -----------------------------------------------
export default function VisualExplanation({ payload }) {
  useEngagementBeacon(payload);
  if (!payload || typeof payload !== "object") return null;
  if (payload.kind !== "visual_explanation") return null;
  const { type, title, confidence, confidence_label, elements, caption } = payload;
  if (!type || !elements) return null;

  // Iter XX.239 — Wrong-vs-Right coaching visuals render an entirely
  // different two-panel layout with realistic cricket poses + red vs
  // green guide overlays + a bottom key-cue. Falls through to the
  // XX.235 renderer when visual_layout !== "wrong_vs_right".
  const isWrongVsRight = type === "technique_diagram"
    && elements.visual_layout === "wrong_vs_right"
    && elements.error_pose && elements.correct_pose;
  const isSingleAnnotated = type === "technique_diagram"
    && elements.visual_layout === "single_annotated"
    && elements.single_pose;

  return (
    <div
      className="mt-2 rounded-xl border border-neutral-800 bg-neutral-950/60 p-3 space-y-2"
      data-testid={`visual-explanation-${type}`}
      data-visual-confidence={confidence}
      data-visual-type={type}
      data-visual-layout={elements.visual_layout || "single"}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-neutral-200">{title}</p>
        <ConfidenceChip confidence={confidence} label={confidence_label} />
      </div>
      {caption && (
        <p className="text-xs text-neutral-400">{caption}</p>
      )}
      {type === "field_map"         && <FieldMapSVG        elements={elements} />}
      {type === "technique_diagram" && isWrongVsRight && (
        <CricketWrongVsRightSVG elements={elements} subtype={payload.subtype} />
      )}
      {type === "technique_diagram" && !isWrongVsRight && isSingleAnnotated && (
        <CricketSingleAnnotatedSVG elements={elements} subtype={payload.subtype} />
      )}
      {type === "technique_diagram" && !isWrongVsRight && !isSingleAnnotated && (
        // Iter XX.235 fallback — dispatch to the cricket-specific renderer when
        // the payload carries the new taxonomy fields; fall back to the
        // XX.234 neutral stick figure otherwise (backward compat).
        payload.cricket_domain || payload.subtype
          ? <CricketTechniqueSVG elements={elements}
                                  subtype={payload.subtype}
                                  orientation={payload.orientation} />
          : <TechniqueDiagramSVG elements={elements} />
      )}
      {type === "action_plan"       && <ActionPlanStrip     elements={elements} />}
    </div>
  );
}
